import React from 'react';
import { Select, Flex, Text, LoadingOrbit, type SelectOption } from '@chatsdet/ui';
import { useOrgMembers, type OrgMember } from '../../hooks/useOrgMembers';

type TeamMemberSelectProps = {
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
  isDisabled?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
};

const UNASSIGNED_ID = '__unassigned__';

function formatMemberLabel(member: OrgMember): string {
  return member.displayName || 'Unknown user';
}

export function TeamMemberSelect({
  selectedUserId,
  onSelect,
  isDisabled,
  placeholder = 'Assign to...',
  label,
  className,
}: TeamMemberSelectProps) {
  const membersQuery = useOrgMembers();
  const members = membersQuery.data?.items ?? [];

  const options: SelectOption[] = React.useMemo(() => {
    const memberOptions: SelectOption[] = members.map((m) => ({
      id: m.userId,
      label: formatMemberLabel(m),
    }));
    return [{ id: UNASSIGNED_ID, label: 'Unassigned' }, ...memberOptions];
  }, [members]);

  const selectedKey = React.useMemo(() => {
    if (!selectedUserId) return UNASSIGNED_ID;
    // Only return the userId if members are loaded and it exists in the list
    const memberExists = members.some((m) => m.userId === selectedUserId);
    return memberExists ? selectedUserId : UNASSIGNED_ID;
  }, [selectedUserId, members]);

  const handleSelectionChange = (key: React.Key | null) => {
    if (key === UNASSIGNED_ID || key === null) {
      onSelect(null);
    } else {
      onSelect(String(key));
    }
  };

  if (membersQuery.isLoading) {
    return (
      <Flex align="center" gap="sm" className={className}>
        <LoadingOrbit size="sm" />
        <Text size="sm" className="text-muted-foreground">
          Loading members...
        </Text>
      </Flex>
    );
  }

  return (
    <Select
      key={selectedUserId ?? 'unassigned'}
      label={label}
      placeholder={placeholder}
      items={options}
      selectedKey={selectedKey}
      onSelectionChange={handleSelectionChange}
      isDisabled={isDisabled || membersQuery.isError}
      className={className}
    />
  );
}
